// Case list (F-cases client half). Flat, newest-first table with a server-side search.
// Server order is authoritative — we never re-sort here.
import { useCallback, useEffect, useState } from 'react';
import {
  type Case,
  type CaseStatus,
  CASE_STATUS_LABELS,
  CASE_PRIORITY_LABELS,
  formatDate,
} from '@alignova/shared';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { t } from '../../i18n/index.ts';
import { Card, Pill } from '../../ui/primitives.tsx';

type Tone = 'neutral' | 'good' | 'bad';

function statusTone(status: CaseStatus): Tone {
  if (status === 'delivered') return 'good';
  if (status === 'cancelled' || status === 'remake') return 'bad';
  return 'neutral';
}

export function CaseList() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<Case[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const load = useCallback((search: string) => {
    const q = search.trim();
    const path = q
      ? `/cases?limit=100&search=${encodeURIComponent(q)}`
      : '/cases?limit=100';
    api
      .get<Case[]>(path)
      .then(setCases)
      .catch(() => setError(t('cases.loadError')));
  }, []);

  // Mount-only: search reloads are driven by the form below (load is a stable useCallback).
  useEffect(() => {
    load(query);
  }, [load]);

  // A case changed elsewhere (or our own queued edit replayed) — pull fresh truth,
  // preserving the current search.
  useLiveEvent('case.updated', () => load(query));

  if (error) return <p className="text-status-urgent-fg">{error}</p>;

  return (
    <div>
      <form
        className="mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          load(query);
        }}
      >
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            load(e.target.value);
          }}
          placeholder={t('cases.searchPlaceholder')}
          aria-label={t('cases.searchPlaceholder')}
          className="w-full max-w-sm rounded border border-line bg-surface px-3 py-2 text-sm"
        />
      </form>
      <Card className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">{t('cases.col.caseNumber')}</th>
              <th className="px-4 py-3 font-medium">{t('cases.col.client')}</th>
              <th className="px-4 py-3 font-medium">{t('cases.col.service')}</th>
              <th className="px-4 py-3 font-medium">{t('cases.col.status')}</th>
              <th className="px-4 py-3 font-medium">{t('cases.col.priority')}</th>
              <th className="px-4 py-3 font-medium">{t('cases.col.due')}</th>
              <th className="px-4 py-3 font-medium">{t('cases.col.updated')}</th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-muted">
                  {t('cases.empty')}
                </td>
              </tr>
            )}
            {cases.map((c) => (
              <tr
                key={c.id}
                onClick={() => navigate(`/cases/${c.id}`)}
                className="cursor-pointer border-b border-line hover:bg-surface-2"
              >
                <td className="px-4 py-2 font-mono text-xs text-accent">{c.caseNumber}</td>
                <td className="px-4 py-2">{c.clientName}</td>
                <td className="px-4 py-2">{c.service}</td>
                <td className="px-4 py-2">
                  <Pill tone={statusTone(c.status)}>{CASE_STATUS_LABELS[c.status]}</Pill>
                </td>
                <td className="px-4 py-2">{CASE_PRIORITY_LABELS[c.priority]}</td>
                <td className="px-4 py-2 font-mono text-xs">{formatDate(c.dueDate)}</td>
                <td className="px-4 py-2 font-mono text-xs">{formatDate(c.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
